import React from 'react';
import _, { noop } from 'lodash';
import { Input, Button, Tooltip, Checkbox } from 'antd';
import { HelpIcon } from 'components/Icons';
import ACDropdown from 'ac-components/ACDropdown';
import { FilterOperationTypeLabel } from 'types/pagination';
import ACButton from 'ac-components/ac-button/ACButton';
import RefreshIcon from 'ac-components/ac-icons/RefreshIcon';
import CheckIcon from 'ac-components/ac-icons/CheckIcon';
import { FilterOperationType } from 'types/api/filterOperationType';

class ColumnFilterSearch extends React.Component{
    inputRef = React.createRef()
    constructor(props){
        super(props);
        this.state = {
            searchText: '',
            filterOperation: FilterOperationType.contains,
            selected: [],
            nullFilter: false,
            showNullFilter: props.hasOwnProperty('nullFilterValue') && !_.isUndefined(props.nullFilterValue),
            changed: false,
        }
        _.bindAll(
            this,
            'searchChanged',
            'applyFilter',
            'clearFilter',
            'toggleNullFilter',
            'operationChange',
            'toggleHideBlankFilter',
            'resetFilter',
        )
    }
    searchChanged(e){
        this.setState({
            searchText: e.target.value,
            changed: true,
        });
    }
    focusInput(){
        if(this.inputRef.current){
            this.focusTimeOut = setTimeout(() => {
                this.inputRef.current.select();
            }, 300);
        }
    }
    componentWillUnmount() {
        if (!_.isUndefined(this.focusTimeOut)) {
          clearTimeout(this.focusTimeOut);
        }
    }
    getSearchText() {
        const { selectedKeys, isMultipleValueSupported } = this.props;
        const isNull = _.isNull(selectedKeys) || _.isNull(selectedKeys[0]);
        const filterOperation = this.props.filterOperationInfo?.[this.props.filterKey];

        let searchText;
        if (isNull) {
            searchText = 'N/A';
        } else {
            if (isMultipleValueSupported) {
                searchText = selectedKeys.join(',');
            } else {
                searchText = selectedKeys[0];
            }
        }

        if (!searchText) {
            searchText = '';
        }

        this.setState({
            searchText,
            nullFilter: isNull,
            changed: false,
            filterOperation,
        }, this.focusInput);
    }
    componentDidMount(){
        this.getSearchText();
    }
    componentDidUpdate(prevProps){
        const { visible } = this.props;
        if(prevProps.visible !== visible){
            if(visible){
                this.getSearchText();
            }
        }
    }
    applyFilter(){
        const {confirm, setSelectedKeys, isMultipleValueSupported, supportFilterOperation, setFilterOperation = noop} = this.props;
        const { searchText, nullFilter, filterOperation } = this.state;
        let filterText;
        if (nullFilter) {
            filterText = [null];
        } else {
            if (_.isEmpty(searchText)) {
                filterText = [];
            } else {
                if (isMultipleValueSupported) {
                    filterText = searchText.split(',').map(text => text.trim());
                } else {
                    filterText = [searchText];
                }
            }
        }
        setSelectedKeys(filterText);
        setFilterOperation(filterOperation);
        confirm(supportFilterOperation ? false : undefined);
    }
    toggleNullFilter(){
        this.setState({ nullFilter: !this.state.nullFilter, searchText: !this.state.nullFilter ? 'N/A' : '', changed: true, });
    }
    toggleHideBlankFilter(){
        this.updateStateAndApply({ 
            nullFilter: !this.state.nullFilter, 
            filterOperation: FilterOperationType.notContains, 
            searchText: '', 
            changed: true, 
        });
    }
    clearFilter(){
        const {clearFilters} = this.props;
        clearFilters();
    }
    operationChange(value){
        const {searchText} = this.state;
        if(!searchText){
            this.setState({
                filterOperation: value
            });
        } else {
            this.updateStateAndApply({
                filterOperation: value
            });
        }
    }
    resetFilter(){
        this.updateStateAndApply({ 
            nullFilter: false, 
            filterOperation: undefined, 
            searchText: '', 
        });
    }
    updateStateAndApply(stateObject){
        this.setState(stateObject, () => {
            setTimeout(() => this.applyFilter(), 50);
        });
    }
    render(){
        const {searchText, nullFilter, showNullFilter} = this.state;
        const { isMultipleValueSupported } = this.props;
        const supportFilterOperation = this.props.supportFilterOperation;
        const filterOperation = this.state.filterOperation || FilterOperationType.contains;
        const filterOperationOptions = [
            FilterOperationType.contains,
            FilterOperationType.notContains,
            FilterOperationType.startWith,
            FilterOperationType.endWith,
        ].map((operation) => ({
            label: FilterOperationTypeLabel[operation],
            value: operation,
            secondaryIcon: operation === filterOperation ? <CheckIcon /> : undefined
        }));

        const _nullFilter = supportFilterOperation ? nullFilter && filterOperation === FilterOperationType.notContains : nullFilter;

        return (
            <div className="column-filter column-filter-text-search">
                {supportFilterOperation && <div className="p-x-xs p-t-xs d-flex justify-content-between gap-10">
                    <ACDropdown
                        mini
                        showArrow
                        items={filterOperationOptions}
                        onOptionClick={this.operationChange}
                        disabled={_nullFilter}
                    >
                        {FilterOperationTypeLabel[filterOperation] || filterOperation}
                    </ACDropdown>
                    {showNullFilter && <div>
                        <Checkbox checked={_nullFilter} onChange={this.toggleHideBlankFilter}>Hide blanks</Checkbox>
                    </div>}
                </div>}
                {!supportFilterOperation && showNullFilter && <div className="m-x-xs m-t-xs d-flex justify-content-between">Search blank records <Checkbox checked={nullFilter} onChange={this.toggleNullFilter} /></div>}
                <div className="p-xs">
                    <Input
                        style={{width: 200}}
                        disabled={_nullFilter}
                        placeholder="Search"
                        value={searchText}
                        onChange={this.searchChanged}
                        onPressEnter={this.applyFilter}
                        ref={this.inputRef}
                        suffix={
                            isMultipleValueSupported && <Tooltip placement="right" title="Use comma as separator to search multiple values"><HelpIcon /> </Tooltip>
                        }
                    />
                </div>
                {supportFilterOperation ? <div className="column-filter-dropdown-action column-filter-dropdown-action-single">
                    <ACButton onClick={this.resetFilter} leftIcon={<RefreshIcon />} size="sm" type="ghost">Reset</ACButton>
                </div> : 
                <div className="ant-table-filter-dropdown-btns">
                    <Button size="small" type="link" onClick={this.clearFilter}>Clear</Button>
                    <Button type="primary" size="small" onClick={this.applyFilter} disabled={!this.state.changed}>Apply</Button>
                </div>}
            </div>
        );
    }
}

export default ColumnFilterSearch;